(ns arthur.events.ui
  "Selection, the active tone, the polygon being drawn, and which timeline rows
  are open.

  THE CREATION TARGET IS A FUNCTION, NOT EDITOR STATE. The primary selection is
  the preferred row, like the active layer in a paint program. The playhead
  resolves that row to the nearest occurrence creation can happen in: an active
  ordinary symbol, a lane insertion surface, an active ancestor, or the open
  symbol. `docs/creating-in.md` is the contract.

  `[:ui :selections]` remains the set copied, deleted or transformed together;
  `[:ui :selection]` is its primary member and therefore the creation anchor.
  Moving the playhead never rewrites either one.

  All of it is `assoc-in` under `:ui`. There is no effect in this namespace and
  there should not be one: an editor's own state is the cheapest thing in the
  app to change and the most expensive to have two copies of."
  (:require [clojure.string :as str]
            [arthur.domain.clip :as clip]
            [arthur.domain.keyframes :as keyframes]
            [arthur.domain.channel :as ch]
            [arthur.domain.clipboard :as clipboard]
            [arthur.domain.correction :as correction]
            [arthur.domain.cut :as cut]
            [arthur.domain.creation :as creation]
            [arthur.domain.gesture :as gesture]
            [arthur.domain.nest :as nest]
            [arthur.domain.outline :as outline]
            [arthur.domain.node :as node]
            [arthur.domain.palette :as pal]
            [arthur.domain.pick :as pick]
            [arthur.domain.span :as span]
            [arthur.domain.symbol :as symbol]
            [arthur.events.edit :as edit]
            [arthur.domain.paint :as paint]
            [arthur.events.playback :as playback]
            [arthur.footage.store :as store]
            [re-frame.core :as rf]))

(defn editing-frame
  "The frame of the open symbol the playhead is showing: the coordinate every
  command in here works in. Every symbol counts in the document's one rate, so it
  is the playhead."
  [db _clip]
  (get-in db [:playback :frame]))

(rf/reg-event-db
 ::rename-node
 ;; An instance has no name of its own — see `clip/node-label` — so renaming one
 ;; renames the symbol it places, everywhere it is placed. Blank removes the
 ;; name, which falls back to the id.
 (fn [db [_ sid id value]]
   (let [document (:clip (store/entry (:clip/current db)))
         n (get-in document [:symbols sid :nodes id])
         path (if-let [of (node/source n)] [:symbols of] [:symbols sid :nodes id])
         value (not-empty (str/trim (str value)))]
     (if (or (nil? n) (nil? (get-in document path)))
       db
       (edit/transaction db #(if value
                               (assoc-in % (conj path :name) value)
                               (update-in % path dissoc :name)))))))

(defn selected
  "`db` with `selection` as its primary active row.

  Selection and row disclosure are independent. A click or trim can select a
  nested item, but only the row's twist button opens or closes it."
  [db selection]
  ;; A captured trim gesture also selects on pointer-up. It must not acquire the
  ;; unrelated side effect of opening every row above the edited clip.
  (-> db
      (assoc-in [:ui :selection] selection)
      (assoc-in [:ui :selections] (if selection [selection] []))
      (update :ui dissoc :retry)))

(rf/reg-event-db ::select (fn [db [_ selection]] (selected db selection)))

(rf/reg-event-db
 ::select-many
 ;; `:selection` remains the primary address used by the inspector and timeline;
 ;; `:selections` is the ordered set used by the stage and batch commands.
 (fn [db [_ selections]]
   (let [xs (vec (distinct (keep identity selections)))]
     (if-let [primary (peek xs)]
       (-> (selected db primary) (assoc-in [:ui :selections] xs))
       (selected db nil)))))

(rf/reg-event-db
 ::toggle-selection
 (fn [db [_ selection]]
   (let [primary (get-in db [:ui :selection])
         many (vec (get-in db [:ui :selections]))
         old (if (some #{primary} many) many (if primary [primary] []))
         xs  (if (some #{selection} old)
               (vec (remove #{selection} old))
               (conj old selection))]
     (if-let [primary (peek xs)]
       (-> (selected db primary) (assoc-in [:ui :selections] xs))
       (selected db nil)))))

(rf/reg-event-db
 ::set-tone
 (fn [db [_ tone]] (assoc-in db [:ui :tone] tone)))

(defn apply-command
  "Commit a successful domain command as one history step. A refused command
  leaves the document and history untouched; an overflow offers an explicit retry.

  A COMMAND NEED NOT NAME A SELECTION. Emptying frames deletes what was selected
  and has nothing sensible to put in its place, so a nil `:selection` leaves the
  selection alone rather than pointing it at a clip nobody asked for."
  [db sid result retry]
  (if-let [why (:refused result)]
    (-> db
        (assoc-in [:project :status] why)
        (assoc-in [:ui :retry] (when (:required-frames result) retry)))
    (let [[_ selected-sid _ path] (get-in db [:ui :selection])
          prefix (if (and (= sid selected-sid) (seq path)) (pop path) [])]
      (cond-> (-> db
                  (edit/transaction (constantly (:clip result)))
                  (update :ui dissoc :retry))
        (:selection result)
        (assoc-in [:ui :selection]
                  [:node sid (:selection result) (conj prefix (:selection result))])))))

(defn apply-correction-command
  "Commit one correction command while keeping the complete row address that
  selected its owner. A refusal changes only the visible status."
  [db result]
  (if-let [why (:refused result)]
    (assoc-in db [:project :status] why)
    (-> db
        (edit/transaction (constantly (:clip result)))
        (update :project merge {:status "edited · unsaved"}))))

(rf/reg-event-db
 ::eye-key
 (fn [db [_ sid repair-id side frame values]]
   (apply-correction-command
    db (correction/eye-key (:clip (store/entry (:clip/current db)))
                           sid repair-id side frame values))))

(rf/reg-event-db
 ::remove-eye-keys
 (fn [db [_ sid repair-id side]]
   (apply-correction-command
    db (correction/remove-eye-keys (:clip (store/entry (:clip/current db)))
                                   sid repair-id side))))

(rf/reg-event-db
 ::borrow-pose
 (fn [db [_ sid spec]]
   (let [entry (store/entry (:clip/current db))]
     (apply-correction-command
      db (correction/borrow-pose (:clip entry) sid
                                  (assoc spec :id (random-uuid)) (:store entry))))))

(rf/reg-event-db
 ::remove-repair
 (fn [db [_ sid id]]
   (apply-correction-command
    db (correction/remove-repair (:clip (store/entry (:clip/current db))) sid id))))

(rf/reg-event-db
 ::add-correction
 (fn [db [_ sid id path spec]]
   (let [clip (:clip (store/entry (:clip/current db)))]
     (apply-correction-command
      db (correction/add clip sid id path (assoc spec :id (random-uuid)))))))

(rf/reg-event-db
 ::remove-correction
 (fn [db [_ sid id path layer-id]]
   (let [clip (:clip (store/entry (:clip/current db)))]
     (apply-correction-command
      db (correction/remove-layer clip sid id path layer-id)))))

(rf/reg-event-db
 ::retry-correction
 (fn [db [_ sid id path layer-id]]
   (let [clip (:clip (store/entry (:clip/current db)))]
     (apply-correction-command
      db (correction/retry-layer clip sid id path layer-id)))))

(rf/reg-event-db
 ::draw-as-lane
 ;; THE ONE PLACE A PERSON CAN ASK FOR THE IMPOSSIBLE. Everything else maintains
 ;; the sequence; this asks a symbol whose clips may already overlap to start
 ;; being one. `span/draw-as-lane` refuses and says what it would have to trim,
 ;; and `::retry` is the one button that says do it.
 (fn [db [_ sid on? trim?]]
   (let [clip (:clip (store/entry (:clip/current db)))
         result (span/draw-as-lane clip sid on? {:trim? trim?})]
     (if-let [why (:refused result)]
       (-> db (assoc-in [:project :status] why)
           (assoc-in [:ui :retry] (when (:required-trim result) [::draw-as-lane sid on? true])))
       (-> db (edit/transaction (constantly (:clip result)))
           (update :ui dissoc :retry))))))

(defn- committed
  "One appending command, as effects: commit it, and look at what it made.

  Seeking is the whole reason these are `-fx` events. An appended cel lands
  past the end of the lane, off the playhead, and a drawing you cannot see is not
  one you can draw in. An inserted one is already under the playhead and the
  seek is a no-op, which is the same rule and not a second one."
  [db sid result retry]
  (let [{clip :clip st :store} (store/entry (:clip/current db))
        path (nth (get-in db [:ui :selection]) 3 nil)
        open (get-in db [:ui :open])
        {:keys [at rate]} (:time (nest/inside clip st open
                                              (if (seq path) (pop path) [])
                                              (editing-frame db clip)))]
    (cond-> {:db (apply-command db sid result retry)}
      (and (:clip result) (:frame result) rate)
      ;; Out of the made node's frames into the open symbol's, which the
      ;; transport counts in too: the first whole frame that shows it.
      (assoc :dispatch [::playback/seek
                        (js/Math.ceil (+ at (/ (:frame result) rate)))]))))

(defn selection-frame
  "The playhead as a frame of the symbol that owns `selection`.

  A timeline selection carries its path from the open symbol. Walking to the
  parent of the selected node crosses every enclosing instance clock, and that
  is the whole of it: a clip of a lane has no parent, so the symbol's own frames
  ARE the sequence's. One clock where there used to be two."
  [clip st open selection frame]
  (let [[_ sid _ path] selection]
    (if (or (= sid open) (not (seq path)))
      (:frame (nest/inside clip st open [] frame))
      (:frame (nest/inside clip st open (pop path) frame)))))

(rf/reg-event-fx
 ::append-drawing
 (fn [{:keys [db]} [_ extent]]
   (let [clip (:clip (store/entry (:clip/current db)))
         [_ sid id] (get-in db [:ui :selection])
         result (span/append-drawing clip sid (random-uuid) (clip/fresh-id clip)
                                     {:extent (or extent :keep)})]
     (committed db sid result [::append-drawing :grow-symbol]))))

(rf/reg-event-fx
 ::reuse-drawing
 (fn [{:keys [db]} [_ extent]]
   (let [clip (:clip (store/entry (:clip/current db)))
         [_ sid id] (get-in db [:ui :selection])
         result (span/reuse-drawing clip sid (random-uuid)
                                    (node/source (get-in clip [:symbols sid :nodes id]))
                                    {:extent (or extent :keep)})]
     (committed db sid result [::reuse-drawing :grow-symbol]))))

(rf/reg-event-fx
 ::duplicate-drawing
 (fn [{:keys [db]} [_ extent deep?]]
   (let [clip (:clip (store/entry (:clip/current db)))
         [_ sid id] (get-in db [:ui :selection])
         result (span/duplicate-drawing clip sid id (random-uuid)
                                        {:extent (or extent :keep) :deep? deep?})]
     (committed db sid result [::duplicate-drawing :grow-symbol deep?]))))

(rf/reg-event-fx
 ::insert-drawing
 ;; The playhead is the position: you scrub to where the drawing goes. The
 ;; symbol's own frames are the sequence's, so there is nothing to convert —
 ;; only an enclosing stepped or looping instance can leave the playhead on no
 ;; single frame of it, which `selection-frame` says by answering nil.
 (fn [{:keys [db]} [_ extent]]
   (let [{clip :clip st :store} (store/entry (:clip/current db))
         selection (get-in db [:ui :selection])
         [_ sid] selection
         at (selection-frame clip st (get-in db [:ui :open]) selection
                             (editing-frame db clip))
         result (if (integer? at)
                  (span/append-drawing clip sid (random-uuid) (clip/fresh-id clip)
                                       {:at at :extent (or extent :keep)})
                  {:refused "the playhead is not on one frame of this symbol"})]
     (committed db sid result [::insert-drawing :grow-symbol]))))

(defn- at-playhead
  "The selected node and the playhead as a frame of the space that node is
  POSITIONED in — its lane's, for a cel; the symbol's, for anything placed
  straight into one. Nil in place of the frame where there is no single one.

  One helper for all three span commands, because `span/host-frame` asks the
  node what it sits in rather than being told, so none of them has to care."
  [db]
  (let [{clip :clip st :store} (store/entry (:clip/current db))
        selection (get-in db [:ui :selection])
        [_ sid id] selection]
    {:clip clip :sid sid :id id
     :node (get-in clip [:symbols sid :nodes id])
     :at (when-let [owner-frame (selection-frame clip st (get-in db [:ui :open]) selection
                                                 (editing-frame db clip))]
           (span/host-frame clip sid id owner-frame))}))

(def ^:private no-frame
  "Said where the playhead maps to no single frame of the space the selection is
  positioned in, which a stepped or looping parent is enough to cause."
  {:refused "the playhead is not on one frame of what holds this"})

(rf/reg-event-db
 ::split
 (fn [db _]
   (let [{:keys [clip sid id at]} (at-playhead db)]
     (apply-command
      db sid (if at (span/split clip sid id at (random-uuid)) no-frame) nil))))

(rf/reg-event-fx
 ::overwrite-drawing
 (fn [{:keys [db]} [_ extent]]
   (let [{clip :clip st :store} (store/entry (:clip/current db))
         selection (get-in db [:ui :selection])
         [_ sid] selection
         at (selection-frame clip st (get-in db [:ui :open]) selection
                             (editing-frame db clip))
         result (if (integer? at)
                  (span/overwrite-drawing clip sid (random-uuid) (clip/fresh-id clip) at
                                          {:extent (or extent :keep)
                                           :remainder-id (random-uuid)})
                  {:refused "the playhead is not on one frame of this symbol"})]
     (committed db sid result [::overwrite-drawing :grow-symbol]))))

(rf/reg-event-db
 ::trim
 (fn [db [_ edge]]
   (let [{:keys [clip sid id at]} (at-playhead db)]
     (apply-command
      db sid (if at (span/trim clip sid id edge at) no-frame) nil))))

(rf/reg-event-db
 ::move
 (fn [db _]
   (let [{:keys [clip sid id at]} (at-playhead db)]
     (apply-command
      db sid (if at (span/move clip sid id at) no-frame) nil))))

(rf/reg-event-db
 ::blank-cel
 ;; The selected cel's own frames, so the range needs no second gesture and
 ;; the case that would split a cel cannot arise.
 (fn [db _]
   (let [{:keys [clip sid node]} (at-playhead db)
         span (node/placed-span node)]
     (apply-command
      db sid (if (and span (every? integer? span))
               (span/blank clip sid span {})
               {:refused "select a clip that starts and ends on whole frames"})
      nil))))

(rf/reg-event-db
 ::make-unique
 (fn [db [_ deep?]]
   (let [clip (:clip (store/entry (:clip/current db)))
         [_ sid id] (get-in db [:ui :selection])]
     (apply-command db sid (span/make-unique clip sid id {:deep? deep?}) nil))))

(rf/reg-event-db
 ::extend-hold
 (fn [db [_ delta extent]]
   (let [clip (:clip (store/entry (:clip/current db)))
         [_ sid id] (get-in db [:ui :selection])
         result (span/extend-hold clip sid id delta {:extent (or extent :keep)})]
     (apply-command db sid result [::extend-hold delta :grow-symbol]))))

(defn lane-here
  "The lane the playhead is in, as `nest/inside` answers it with its `:path`
  from the open symbol: the nearest lane the selection is in, at any depth —
  so a stroke just drawn in a cel counts as being in that cel's lane — or else
  the lane the selection would draw into. Nil where there is none."
  [db]
  (let [{clip :clip st :store} (store/entry (:clip/current db))
        open (get-in db [:ui :open])
        f (editing-frame db clip)
        [kind _ _ path :as selection] (get-in db [:ui :selection])
        lane-at (fn [p]
                  (when-let [in (nest/inside clip st open p f)]
                    (when (symbol/lane? (clip/symbol clip (:sid in)))
                      (assoc in :path p))))]
    (or (when (= :node kind)
          (some #(lane-at (pop %)) (take-while seq (iterate pop (vec path)))))
        (let [t (creation/target clip st open selection f)]
          (when (= :lane (:kind t)) (lane-at (:path t)))))))

(defn span-path
  "The row whose span = and − move: the selection's, or — inside a lane — the
  cel the selection is in, so a stroke just drawn holds its drawing longer."
  [db]
  (let [path (vec (nth (get-in db [:ui :selection]) 3 nil))
        lane (some-> (lane-here db) :path count)]
    (when (seq path)
      (if (and lane (< lane (count path))) (subvec path 0 (inc lane)) path))))

(rf/reg-event-fx
 ::drawing-here
 ;; K and ⇧K in a lane: see `span/drawing-at`. The new cel is selected and the
 ;; playhead put on it, because it is there to be drawn in. `lane` is
 ;; `lane-here`'s answer unless the caller has already chosen one.
 (fn [{:keys [db]} [_ copy? lane]]
   (let [clip (:clip (store/entry (:clip/current db)))
         {:keys [sid frame path time]} (or lane (lane-here db))
         id (random-uuid)
         result (if sid
                  (span/drawing-at clip sid frame id (clip/fresh-id clip)
                                   {:copy? copy? :extent :grow-symbol})
                  {:refused "select a lane, or a drawing in one"})]
     (if-let [why (:refused result)]
       {:db (assoc-in db [:project :status] why)}
       (cond-> {:db (-> db
                        (edit/transaction (constantly (:clip result)))
                        (selected [:node sid id (conj path id)]))}
         time (assoc :dispatch [::playback/seek
                                (js/Math.ceil (+ (:at time) (/ (:frame result) (:rate time))))]))))))

(rf/reg-event-db
 ::key-selected
 ;; K on anything that is not in a lane: Blender's I.
 (fn [db _]
   (let [{clip :clip st :store} (store/entry (:clip/current db))
         f (editing-frame db clip)
         places (keep (fn [[kind _ _ path]]
                        (when (and (= :node kind) (seq path))
                          (nest/placement clip st (get-in db [:ui :open]) path f)))
                      (get-in db [:ui :selections]))]
     (if (empty? places)
       (assoc-in db [:project :status] (if (seq (get-in db [:ui :selections]))
                                         "what is selected is not on screen at this frame"
                                         "select something to key"))
       (edit/edit db #(reduce (fn [c {:keys [sid id frame]}]
                                (update-in c [:symbols sid :nodes id] node/key-at frame st))
                              % places))))))

(rf/reg-event-fx
 ::retry
 (fn [{:keys [db]} _]
   (if-let [event (get-in db [:ui :retry])]
     {:db (update db :ui dissoc :retry) :dispatch event}
     {})))


(rf/reg-event-db
 ::set-row-expanded
 (fn [db [_ path expanded?]]
   ;; The disclosure control says what the next state is instead of asking us
   ;; to invert whatever happens to be in app-db by the time its event runs.
   ;; A structural drop may reveal this path between pointer-down and click;
   ;; blindly toggling then immediately closed the row whose triangle was still
   ;; painted as closed.
   (update-in db [:ui :expanded]
              (fn [paths]
                ((if expanded? conj disj) (set paths) path)))))

(rf/reg-event-db
 ::solo
 (fn [db [_ path more?]]
   ;; Per open symbol, because a row path only means something from the symbol
   ;; it was walked from. A click solos that row alone, or un-solos it if it
   ;; already was; a shift-click adds it to or takes it out of the ones soloed.
   (update-in db [:ui :solo (get-in db [:ui :open])]
              (fn [on]
                (cond more?             (if (contains? on path) (disj on path) (conj (set on) path))
                      (= on #{path})    #{}
                      :else             #{path})))))

(rf/reg-event-db
 ::show-trace
 ;; One tracing layer on or off — `layer` is `[symbol-id node-id]`, see
 ;; `arthur.db/tracing`. Editor state, like solo: not an undo step, not sent to
 ;; a collaborator, and it cannot reach an export.
 ;;
 ;; SWITCHING ONE ON SWITCHES TRACING ON. A layer just asked for must not stay
 ;; invisible behind the global switch somebody forgot was off; switching one
 ;; off leaves the global switch alone. One event, so the inspector, a timeline
 ;; row and a face's section cannot disagree about it.
 (fn [db [_ layer on?]]
   (update-in db [:ui :tracing]
              #(cond-> (update % :hidden (fnil (if on? disj conj) #{}) layer)
                 on? (assoc :on? true)))))

(defn- trace-layers
  "The tracing layers in symbol `sid` and everything it places, as
  `arthur.db/tracing` names them: a placement of a tracing symbol is
  `[sid id]`, a face's footage `[face :plate]`. Only nodes `keep?` says yes to
  are looked in at the top; below that, everything is."
  [clip sid keep?]
  (let [nodes (:nodes (clip/symbol clip sid))]
    (into (if (get nodes :plate) #{[sid :plate]} #{})
          (mapcat (fn [[id n]]
                    (when (keep? id)
                      (let [src (node/source n)]
                        (cond (and src (clip/trace? (clip/symbol clip src))) [[sid id]]
                              src (trace-layers clip src (constantly true)))))))
          nodes)))

(rf/reg-event-db
 ::trace-selection
 ;; ⇧T: tracing on for what is in the selection only — the selected node, what
 ;; it places, its children — and every other layer off. Again, all of them.
 (fn [db _]
   (let [clip (:clip (store/entry (:clip/current db)))
         [kind sid id] (get-in db [:ui :selection])
         nodes (:nodes (clip/symbol clip sid))
         mine (if (and (= :node kind) id)
                (trace-layers clip sid #(some #{id} (symbol/lineage nodes %)))
                #{})
         every (into #{} (mapcat #(trace-layers clip % (constantly true))) (keys (:symbols clip)))
         solo (assoc (get-in db [:ui :tracing]) :on? true :hidden (reduce disj every mine))]
     (cond
       (empty? mine) (assoc-in db [:project :status] "no tracing in the selection")
       (= solo (get-in db [:ui :tracing])) (assoc-in db [:ui :tracing :hidden] #{})
       :else (assoc-in db [:ui :tracing] solo)))))

(rf/reg-event-db
 ::tracing-on
 ;; Every tracing layer at once, from the bar above the stage.
 (fn [db [_ on?]] (assoc-in db [:ui :tracing :on?] (boolean on?))))

(rf/reg-event-db
 ::tracing-opacity
 (fn [db [_ opacity]] (assoc-in db [:ui :tracing :opacity] opacity)))

(rf/reg-event-db
 ::smart-picking
 ;; THE EDITOR'S, not the document's. The setting in docs/frame-selection.md is
 ;; the document's and wants a leaf and a round trip before it can be written;
 ;; until then this switches what the stage shows so the snap can be looked at,
 ;; and an export is unaffected. Which is why it is not undoable and not synced.
 ;;
 ;; Keyed by FACE, as `[:ui :trace :faces]` is and for the same reason: what it
 ;; reads is stored on the face's own nodes, so it is the same answer wherever
 ;; that face is placed.
 (fn [db [_ face on]]
   (update-in db [:ui :smart] #(let [on? (boolean on)]
                                 ((if on? conj disj) (set %) face)))))

;; ---------------------------------------------------------------------------
;; clipboard and local duplication

(defn- current-selections [db]
  (let [primary (get-in db [:ui :selection])
        many (vec (get-in db [:ui :selections]))]
    (cond (nil? primary) []
          (some #{primary} many) many
          :else [primary])))

(defn- command-refused [db why]
  (assoc-in db [:project :status] why))

(defn- select-results [db addresses]
  (let [addresses (vec addresses)]
    (if-let [primary (peek addresses)]
      (-> db
          (assoc-in [:ui :selection] primary)
          (assoc-in [:ui :selections] addresses)
          (update :ui dissoc :retry))
      (-> db
          (assoc-in [:ui :selection] nil)
          (assoc-in [:ui :selections] [])))))

(rf/reg-event-db
 ::copy
 (fn [db _]
   (let [clip (:clip (store/entry (:clip/current db)))
         r (clipboard/snapshot clip (current-selections db))]
     (if-let [why (:refused r)]
       (command-refused db why)
       (-> db
           (assoc-in [:ui :clipboard] (:clipboard r))
           (assoc-in [:project :status]
                     (str "copied " (count (get-in r [:clipboard :items])))))))))

(rf/reg-event-db
 ::cut
 (fn [db _]
   (let [clip (:clip (store/entry (:clip/current db)))
         snapped (clipboard/snapshot clip (current-selections db))]
     (if-let [why (:refused snapped)]
       (command-refused db why)
       (let [r (clipboard/cut clip (:clipboard snapped))]
         (if-let [why (:refused r)]
           (command-refused db why)
           (-> db
               (assoc-in [:ui :clipboard] (:clipboard snapped))
               (edit/transaction (constantly (:clip r)))
               (select-results []))))))))

(defn- paste-anchor
  "The selection a paste goes by: the selection, except in Figma's two cases.

  A paste goes into what is selected and selects what it made. Pasting again
  onto your own paste therefore means another one beside it, not one inside the
  last — so while the selection is still exactly what the last paste made, it
  goes where that one went. And pasting with the very thing you copied still
  selected means a copy of it beside it: inside itself is a cycle, and was
  refused. Select anything else and paste follows the selection again."
  [db]
  (let [{:keys [made from]} (get-in db [:ui :paste])
        primary (get-in db [:ui :selection])
        path (nth primary 3 nil)
        copied (into #{} (map :path) (get-in db [:ui :clipboard :items]))]
    (cond
      (and made (= made (current-selections db))) from
      (and (seq path) (contains? copied path)) (let [up (pop (vec path))]
                                                 [:node nil (peek up) up])
      :else primary)))

(defn paste-destination
  "The effective creation context at the playhead, in the destination's frames."
  [db clip st]
  (creation/target clip st (get-in db [:ui :open])
                   (paste-anchor db) (editing-frame db clip)))

(rf/reg-event-db
 ::paste
 (fn [db _]
   (let [{clip :clip st :store} (store/entry (:clip/current db))
         payload (get-in db [:ui :clipboard])
         destination (paste-destination db clip st)]
     (if-let [why (:refused destination)]
       (command-refused db why)
       (let [{:keys [sid path frame]} destination
             r (clipboard/paste clip payload sid frame {:fresh-id random-uuid})]
         (if-let [why (:refused r)]
           (command-refused db why)
           (let [made (mapv (fn [id] [:node sid id (conj path id)]) (:roots r))]
             (-> db
                 (edit/transaction (constantly (:clip r)))
                 (select-results made)
                 (assoc-in [:ui :paste] {:from (paste-anchor db) :made made})))))))))

(defn- duplicate-selected [db unique?]
  (let [clip (:clip (store/entry (:clip/current db)))
        snapped (clipboard/snapshot clip (current-selections db))]
    (if-let [why (:refused snapped)]
      (command-refused db why)
      (let [r (clipboard/duplicate clip (:clipboard snapped)
                                   {:fresh-id random-uuid :unique? unique?})]
        (if-let [why (:refused r)]
          (command-refused db why)
          (-> db
              (edit/transaction (constantly (:clip r)))
              (select-results
               (mapv (fn [{:keys [sid id path]}] [:node sid id path]) (:roots r)))))))))

(rf/reg-event-db ::duplicate (fn [db _] (duplicate-selected db false)))
(rf/reg-event-db ::duplicate-unique (fn [db _] (duplicate-selected db true)))

;; ---------------------------------------------------------------------------
;; drawing
;;
;; THE TOOL IS WHAT A DRAG ON THE STAGE DOES; the selection is what it does it
;; to. One tool at a time, each with a key, as in Photoshop, Illustrator and
;; Flash: V selects and transforms, P is the pen, B the brush, E the eraser.
;; Editing a shape's points is the pen ON that shape — Figma's vector edit
;; mode — so there is no separate points mode to be in.
;;
;; The pen's draft is in app-db rather than in a ratom because the stage draws
;; it, the canvas fills it and the options bar counts it — and because a
;; half-drawn shape surviving a hot reload is worth more than the handful of
;; dispatches it costs. Clicks are rare; this is not the drag path. `:hover` is
;; where the pointer is while a draft is open, so the canvas can fill the shape
;; the next click would make.

(def tools #{:select :pen :brush :eraser})

(declare finishing-polygon)

(defn with-tool
  "`db` on `tool`. Leaving the pen commits what it was drawing, as Esc does: an
  unfinished polygon is not a thing the document can hold."
  [db tool]
  (if (contains? tools tool)
    (-> (if (= tool (get-in db [:ui :tool])) db (finishing-polygon db))
        (update :ui merge {:tool tool :draft []})
        (update :ui dissoc :hover))
    db))

(rf/reg-event-db ::set-tool (fn [db [_ tool]] (with-tool db tool)))

(rf/reg-event-db
 ::cancel-polygon
 (fn [db _] (update (assoc-in db [:ui :draft] []) :ui dissoc :hover)))

(rf/reg-event-db
 ::hover
 (fn [db [_ p]] (if p (assoc-in db [:ui :hover] p) (update db :ui dissoc :hover))))

(rf/reg-event-fx
 ::tool-key
 ;; A key from `ui/tools`, decided here against the state it changes, and
 ;; passed on as the event the button for it would send.
 (fn [{:keys [db]} [_ k]]
   (let [{:keys [tool draft brush]} (:ui db)
         size (or brush 6)
         by   (if (< size 10) 1 2)
         ev   (case k
                "v" [::set-tool :select]
                "p" [::set-tool :pen]
                "b" [::set-tool :brush]
                "e" [::set-tool :eraser]
                "[" [::brush-size (- size by)]
                "]" [::brush-size (+ size by)]
                "enter"  (cond (seq draft) [::finish-polygon]
                               (#{:select nil} tool) [::set-tool :pen])
                "escape" (when (= :pen tool)
                           (if (seq draft) [::finish-polygon] [::set-tool :select]))
                nil)]
     (if ev {:fx [[:dispatch ev]]} {}))))

(rf/reg-event-db
 ::brush-size
 (fn [db [_ size]] (assoc-in db [:ui :brush] (-> size js/Math.round (max 1) (min 64)))))

(defn new-lane-drawing
  "Create the one-frame drawing cel implied by drawing with a lane row active,
  where the lane shows nothing at the playhead — see `polygon-landing`.

  `overwrite-drawing` rather than `append-drawing`, because a gap already has
  the room. Appending RIPPLES everything after it later by the new clip's
  duration — that is what `insert` means, and it is a different intention.
  THE CLIP'S PATH IS THE SYMBOL'S PATH PLUS ONE STEP, because a clip of a lane
  is an ordinary row of the symbol holding it — which is the addressing `rows`
  hands the timeline and `trail` reads back."
  [clip {:keys [sid frame path]}]
  (if-not (integer? frame)
    {:refused "the playhead is not on one frame of this symbol"}
    (let [cel-id (random-uuid)
          made   (span/overwrite-drawing clip sid cel-id (clip/fresh-id clip) frame
                                         {:extent :grow-symbol
                                          :remainder-id (random-uuid)})]
      (if (:refused made)
        made
        {:clip (:clip made) :sid sid :path (conj (vec path) cel-id)}))))

(defn- showing
  "The id of the clip of lane `sid` that is on screen at the lane's frame `f`,
  or nil where the lane shows nothing."
  [clip sid f]
  (some (fn [[id n]]
          (when-let [[lo hi] (node/placed-span n)]
            (when (and (node/source n) (<= lo f) (< f hi)) id)))
        (get-in clip [:symbols sid :nodes])))

(defn polygon-landing
  "Choose the document and row path a finished polygon is drawn into.

  DRAWING IN A LANE DRAWS INTO WHAT IT SHOWS. With the lane as the target, the
  drawing on screen at the playhead is what you are drawing on, so the shape
  goes into it — exactly as if that cel were selected. Only where the lane shows
  nothing is a new cel made; a new drawing over an old one is K's job, and ⇧K's,
  not a side effect of the brush."
  [clip st db]
  (let [into (creation/target clip st (get-in db [:ui :open])
                              (get-in db [:ui :selection]) (editing-frame db clip))
        cel (when (= :lane (:kind into)) (showing clip (:sid into) (:frame into)))]
    (cond
      cel {:clip clip :path (conj (vec (:path into)) cel) :lane? false}
      (= :lane (:kind into)) (assoc (new-lane-drawing clip into) :lane? true)
      :else {:clip clip :sid (:sid into) :path (:path into) :lane? false})))

(defn beginning-polygon
  "Enter polygon mode, first materializing a new drawing at the playhead when
  the effective target is a lane.

  Creating here, rather than when the polygon is finished, means the drawing is
  already the current cel while points are being placed. Cancelling the polygon
  cancels only the draft; the explicitly started drawing remains."
  [db]
  (let [{clip :clip st :store} (store/entry (:clip/current db))
        landing (polygon-landing clip st db)
        source (:clip landing)
        made? (and source (not (identical? clip source)))
        db' (cond
              (:refused landing)
              (update db :project merge {:status (:refused landing)})

              made?
              (-> db
                  (edit/transaction (constantly source))
                  ;; THE LANE'S SYMBOL, NOT THE OPEN ONE. The clip just made
                  ;; lives in the symbol the lane draws, which is only the open
                  ;; symbol when the lane IS what is open. Naming the wrong one
                  ;; left a selection that looked up to nothing, so the
                  ;; inspector, the breadcrumb and every span command went
                  ;; blank on a drawing that had just been created.
                  (selected [:node (:sid landing)
                             (peek (:path landing)) (:path landing)]))

              :else db)]
    (if (:refused landing) db' (assoc-in db' [:ui :draft] []))))

(rf/reg-event-db
 ::add-draft-point
 ;; The first point is where the drawing begins: a lane gets its new cel then,
 ;; so it is the current one while the rest are placed.
 (fn [db [_ x y]]
   (if (= :pen (get-in db [:ui :tool]))
     (update-in (if (seq (get-in db [:ui :draft])) db (beginning-polygon db))
                [:ui :draft] (fnil into []) [x y])
     db)))

(defn- landing-into
  "Where shapes drawn on the stage go, and their points re-expressed there:
  `{:clip :sid :frame :down :rings}` with each of `rings` in that symbol's
  coordinates, or `{:refused why}`. `down` is the row path to draw inside, or
  nil for the creation target, which a lane makes a new cel for."
  [db down rings]
  (let [open (get-in db [:ui :open])
        {clip :clip st :store} (store/entry (:clip/current db))
        landing (if down {:clip clip :path down} (polygon-landing clip st db))
        source  (:clip landing)
        inside  #(nest/drawn-inside source st open (:path landing) (editing-frame db source) %)
        at      (when-not (:refused landing) (inside []))]
    (cond
      (:refused landing) landing
      (nil? (:sid at)) {:refused (if (:lane? landing)
                                   "this sequence is not on screen at this frame"
                                   "what you are drawing into is not on screen at this frame")}
      :else {:clip source :sid (:sid at) :frame (:frame at) :down (:path landing)
             :rings (mapv (comp :pts inside) rings)})))

(defn- finishing-polygon
  "`db` with the pen's draft committed as a shape, when it has three points."
  [db]
  (let [draft (get-in db [:ui :draft])
        {:keys [refused clip sid frame down rings]} (when (<= 6 (count draft))
                                                       (landing-into db nil [draft]))]
    (cond
      (< (count draft) 6) (update (assoc-in db [:ui :draft] []) :ui dissoc :hover)
      refused (update db :project merge {:status refused})
      ;; `random-uuid` is the one impurity in this namespace, and it is here
      ;; rather than in `domain/paint` for the reason `clip/place-symbol` spells
      ;; out: a node's id is its identity in the saved document, so the pure
      ;; layer must be handed one rather than invent one. If replaying the event
      ;; log ever has to reproduce a document exactly, this becomes a cofx.
      ;;
      ;; NOTHING IS OPENED HERE. Expansion is the twist triangle's business; the
      ;; shape is selected and on the stage, which is where you were looking.
      :else
      (let [id (keyword (str "paint-" (random-uuid)))]
        (-> db
            (edit/transaction
             (fn [_] (paint/new-shape clip sid id frame (first rings) (get-in db [:ui :tone]))))
            (assoc-in [:ui :draft] [])
            (update :ui dissoc :hover :last)
            (selected [:node sid id (conj down id)]))))))

;; Closing on the first point, Enter, Esc and leaving the pen all commit, as in
;; Figma; the pen stays the tool, as it does in Photoshop.
(rf/reg-event-db ::finish-polygon (fn [db _] (finishing-polygon db)))

;; ---------------------------------------------------------------------------
;; brush and eraser
;;
;; A stroke arrives as the pieces `domain/outline` traced off its mask, in stage
;; pixels, holes and all. Each becomes a shape fitted to within `:fit` pixels
;; of what was painted — points where it bends, none on a straight run. What
;; it was made from is kept in `[:ui :last]`, Blender's Adjust Last Operation,
;; so the fit can be changed afterwards from the trace rather than from the
;; points already thrown away, until something else is done.

(def default-fit 1)

(defn- stroke-points [pieces fit]
  (mapv #(outline/polygon % fit) pieces))

(rf/reg-event-db
 ::fit
 (fn [db [_ fit]] (assoc-in db [:ui :fit] (-> fit (max 0.5) (min 8)))))

(defn- saved [db] (:clip (store/entry (:clip/current db))))

(defn- fresh-ids [] (repeatedly #(keyword (str "paint-" (random-uuid)))))

(defn- erasing
  "`db` with `pieces` cut out of the shapes at `paths`. See `cut/erase`."
  [db before pieces fit paths ids]
  (let [{st :store} (store/entry (:clip/current db))
        cutters (mapv #(outline/rings-of % fit) pieces)]
    (edit/transaction db (fn [_] (cut/erase before st (get-in db [:ui :open])
                                            (editing-frame db before) paths cutters ids)))))

(rf/reg-event-db
 ::stroke
 ;; The brush draws in the tone, into the creation target, as the pen does.
 ;; With `paths` it is the eraser's, and cuts the shapes they lead to.
 (fn [db [_ {:keys [pieces paths]}]]
   (let [fit (get-in db [:ui :fit] default-fit)
         before  (saved db)]
     (if paths
       (let [ids (vec (take 64 (fresh-ids)))
             db  (erasing db before pieces fit paths ids)]
         (assoc-in db [:ui :last] {:kind :eraser :pieces pieces :paths paths :ids ids
                                   :fit fit :before before :after (saved db)}))
       (let [{:keys [refused clip sid frame] :as at} (when (seq pieces)
                                                        (landing-into db nil (stroke-points pieces fit)))
             ids (vec (take (count pieces) (fresh-ids)))]
         (cond
           (empty? pieces) db
           refused (update db :project merge {:status refused})
           :else
           (let [db (-> db
                        (edit/transaction
                         (fn [_] (reduce (fn [c [id pts]]
                                           (paint/new-shape c sid id frame pts (get-in db [:ui :tone])))
                                         clip (map vector ids (:rings at)))))
                        (selected [:node sid (peek ids) (conj (:down at) (peek ids))]))]
             (assoc-in db [:ui :last] {:kind :brush :pieces pieces :fit fit
                                       :down (:down at) :sid sid :frame frame :ids ids
                                       :after (saved db)}))))))))

(rf/reg-event-db
 ::adjust-last
 ;; The last stroke's fit, and the next one's: one setting, in the options
 ;; bar and in the panel, as Blender's redo panel writes back to the tool.
 (fn [db [_ fit]]
   (let [{:keys [kind pieces paths ids down sid frame before after]} (get-in db [:ui :last])
         ;; Only while the document is still what the stroke left it as.
         db (assoc-in db [:ui :fit] fit)]
     (if-not (and pieces (identical? after (saved db)))
       db
       (let [db (if (= :eraser kind)
                  (erasing db before pieces fit paths ids)
                  (let [at (landing-into db down (stroke-points pieces fit))]
                    (edit/transaction
                     db (fn [c] (reduce (fn [c [id pts]] (paint/place-points c sid id frame pts))
                                        c (map vector ids (:rings at)))))))]
         (update-in db [:ui :last] assoc :fit fit :after (saved db)))))))

(rf/reg-event-db
 ::insert-vertex
 ;; The new point is the picked one, so a click that added it in the wrong
 ;; place is taken back with Delete.
 (fn [db [_ sid id i t]]
   (-> (edit/transaction db #(paint/insert-vertex % sid id i t))
       (assoc-in [:ui :vertex] [sid id (inc i)]))))

(defn- deleting-vertex
  "Point `i` of the shape gone, and the one before it picked, so Delete held or
  pressed again goes on taking points — as Backspace takes characters — and
  never falls through to the shape itself. A triangle keeps its three."
  [db sid id i]
  (let [{clip :clip} (store/entry (:clip/current db))
        n (some-> (get-in clip [:symbols sid :nodes id :channels paint/geometry :keys])
                  vals first count (quot 2))]
    (cond-> (assoc-in db [:ui :vertex] [sid id i])
      (and n (< 3 n)) (-> (edit/transaction #(paint/delete-vertex % sid id i))
                          (assoc-in [:ui :vertex] [sid id (mod (dec i) (dec n))])))))

(rf/reg-event-db
 ::delete-vertex
 (fn [db [_ sid id i]] (deleting-vertex db sid id i)))

(rf/reg-event-db
 ::pick-vertex
 ;; The point Delete takes away: the last one pressed on the pen.
 (fn [db [_ sid id i]] (assoc-in db [:ui :vertex] [sid id i])))

(rf/reg-event-db
 ::set-knob
 (fn [db [_ scope id knob value]]
   (assoc-in db [:ui :knobs [scope id knob]] value)))

(rf/reg-event-db
 ::toggle-auto-key
 (fn [db _]
   (update-in db [:ui :auto-key?] not)))

(declare record-auto-frame)

;; ---------------------------------------------------------------------------
;; a new symbol or lane

(defn- create-container
  "Create and place one empty symbol. Lane creation is explicit: `lane?` marks
  the new symbol for linear timeline display; ordinary symbol creation never
  does. Both operations place the new symbol through the same span command, so
  a selected lane still applies its claim-time rule."
  [db where lane?]
  (let [{document :clip st :store} (store/entry (:clip/current db))
        open (get-in db [:ui :open])
        playhead (editing-frame db document)
        into (if (= :top where)
               (creation/target document st open nil playhead)
               (creation/target document st open (get-in db [:ui :selection]) playhead))
        ;; A lane is the open symbol's timeline, not an insert that happens to
        ;; begin where the playhead was when it was made. Giving that wrapper
        ;; its whole parent window makes the row's promise true: it is a
        ;; destination at every frame. Resolve the parent at the ACTUAL playhead
        ;; first; using frame zero for resolution would incorrectly fall out of
        ;; every nested symbol that begins later in the open context.
        {host :sid resolved-at :frame down :path destination-kind :kind} into
        at (if lane? 0 resolved-at)
        sid (clip/fresh-id document)
        instance-id (random-uuid)
        end (clip/frames document host)
        result (cond
                 (nil? host)
                 {:refused "what you are adding to is not on screen at this frame"}
                 (not (integer? at))
                 {:refused "the playhead is not on one frame of this symbol"}
                 (or (nil? end) (>= at end))
                 {:refused "the playhead is past the end of this symbol"}
                 :else
                 (let [new-symbol (cond-> {:id sid
                                           ;; A fresh cel is one frame for
                                           ;; frame-by-frame work. In an
                                           ;; ordinary composition the new
                                           ;; symbol keeps the available window.
                                           :frames (if (= :lane destination-kind)
                                                     1 (- end at))
                                           :nodes {}}
                                    lane? (assoc :display :lane))
                       seeded (assoc-in document [:symbols sid] new-symbol)]
                   (span/place-symbol seeded st host instance-id sid at
                                      {:extent :grow-symbol
                                       :remainder-id (random-uuid)
                                       ;; Held like a drawing cel, so
                                       ;; stretching it shows what is drawn
                                       ;; in it across the whole cel.
                                       :speed (if (and (= :lane destination-kind)
                                                       (not lane?))
                                                0 1)})))]
    (if-let [why (:refused result)]
      (update db :project merge {:status why})
      (let [selection [:node host instance-id (conj (vec down) instance-id)]]
        (-> db
            (edit/transaction (constantly (:clip result)))
            (selected selection)
            (update-in [:ui :expanded] (fnil clojure.core/into #{})
                       (rest (reductions conj [] down))))))))

(rf/reg-event-db
 ::new-symbol
 (fn [db [_ where]]
   (create-container db where false)))

(rf/reg-event-db
 ::new-lane
 (fn [db [_ where]]
   ;; A lane is an explicit persistent row across the effective target symbol.
   ;; The playhead validates that nested target; the row itself begins at zero.
   (create-container db (or where :inside) true)))

(rf/reg-event-fx
 ::new
 ;; N and its variants: a new symbol or lane where the selection says, or at
 ;; the top of the tab (`:top`).
 ;;
 ;; A NEW SYMBOL IN A LANE IS A NEW DRAWING, so there it is ⇧K — one meaning
 ;; for "a new empty thing in a lane", rather than two that differ in how
 ;; much of the hold they take. A lane made in a lane is still a lane.
 (fn [{:keys [db]} [_ where lane?]]
   (let [{document :clip st :store} (store/entry (:clip/current db))
         into (creation/target document st (get-in db [:ui :open])
                               (when-not (= :top where) (get-in db [:ui :selection]))
                               (editing-frame db document))]
     (if (and (not lane?) (= :lane (:kind into)))
       {:fx [[:dispatch [::drawing-here false into]]]}
       {:db (create-container db where lane?)}))))

;; ---------------------------------------------------------------------------
;; a drop in flight
;;
;; `[:ui :drop]` is where a drag out of the pool would land: `:where` is :stage or
;; :timeline, `:frame` the frame of the open symbol it would start on, and
;; `:point` the stage pixel under the pointer when that is the stage. `:label`
;; and `:frames` ride along so the timeline can draw the preview row without
;; reaching into the drag. Only written when it CHANGES — dragover fires every few
;; milliseconds whether the pointer moved or not.

(rf/reg-event-db
 ::drop-hover
 (fn [db [_ hover]]
   (if (= hover (get-in db [:ui :drop])) db (assoc-in db [:ui :drop] hover))))

(rf/reg-event-db
 ::drop-clear
 (fn [db _] (update db :ui dissoc :drop)))

(defn drop-destination-at
  "Which symbol a drop on `target` lands in and on which of its frames:
  `{:clip :sid :at :path}`, or `{:refused why}`.

  ONE RULE AND EVERY DROP ASKS IT — a symbol from the pool, a sound, and a video
  brought in as a take alike. The pointer names a ROW, `target`, and a row leads
  into a symbol exactly where it names an instance, which is what `nest/inside`
  already answers; with nothing under the pointer it is the open symbol. Nothing
  is created to receive the drop: the symbol IS the container, so the first drop
  into one is the same operation as the second.

  `frame` is a frame of the OPEN symbol — the ruler's own unit, which is what the
  pointer answered with — and `:at` is that frame carried down into the
  destination's. Nothing on this path crosses the output grid.

  `:clip` is handed back unchanged so all placement callers consume the same
  result shape."
  [document st open frame target]
  (let [[_ sid id selected-path] target
        path0 (vec (or (seq selected-path) (when id [id])))
        path (cond
               (nil? target) []
               (= :instance (get-in document [:symbols sid :nodes id :kind])) path0
               :else (vec (butlast path0)))
        ;; A ROW WITH NO PATH IS THE SYMBOL IT NAMES, on this very frame: the
        ;; open symbol's own row, and a palette track's, which hangs off its
        ;; owner by `:palette-track` instead of being placed in it and so is
        ;; reached by naming it rather than by walking to it.
        from (if (seq path) open (or sid open))
        {destination :sid at :frame matrix :matrix}
        (nest/inside document st from path frame)]
    (cond
      (nil? destination) {:refused "what you are dropping into is not on screen at this frame"}
      (clip/trace? (clip/symbol document destination))
      {:refused "a tracing layer is a picture to draw over — nothing goes inside it"}
      (not (integer? at)) {:refused "the drop is not on one frame of that symbol"}
      :else {:clip document :sid destination :at at :path path :matrix matrix})))

(defn drop-destination
  "`drop-destination-at` from the symbol currently open in `db`."
  [db document st frame target]
  (drop-destination-at document st (get-in db [:ui :open]) frame target))

(defn creation-destination
  "A stage creation destination from the primary selection and playhead."
  [db document st frame]
  (let [{:keys [sid path matrix] at :frame}
        (creation/target document st (get-in db [:ui :open])
                         (get-in db [:ui :selection]) frame)]
    (cond
      (nil? sid) {:refused "nothing under the playhead can contain this"}
      (not (integer? at)) {:refused "the playhead is not on one frame of that symbol"}
      :else {:clip document :sid sid :at at :path path :matrix matrix})))

(defn destination-point
  "A point in the current tab transformed into `where`'s local coordinates."
  [{:keys [matrix]} point]
  (if-let [inv (and point matrix (node/invert matrix))]
    (let [out (js/Float64Array. 2)]
      (node/apply-pt! out 0 inv (first point) (second point))
      [(aget out 0) (aget out 1)])
    point))

(defn landed
  "`db` after a drop that produced `result`, with `uuid` selected."
  [db {:keys [sid path]} uuid result]
  (if-let [why (:refused result)]
    (-> db (update :ui dissoc :drop) (update :project merge {:status why}))
    (-> db
        (update :ui dissoc :drop)
        (edit/transaction (constantly (:clip result)))
        (selected [:node sid uuid (conj (vec path) uuid)]))))

(defn- lane-of-palette-row
  "`document` and `target` with the palette row of a symbol that has no palette
  track yet replaced by that track, created here.

  THE ONE ROW THAT MAY NAME A LANE THAT DOES NOT EXIST, and the only thing
  anything downstream needs to know about palettes: materializing it turns that
  row into an ordinary lane row, so one gesture resolves and places through one
  rule. The track borrows its owner's clock because it is read frame for frame
  against it."
  [document target]
  (if-let [owner (when (= :arthur.ui.timeline/palette-track (first target))
                   (second target))]
    (let [track (or (get-in document [:symbols owner :palette-track])
                    (clip/fresh-id document))]
      [(-> document
           (assoc-in [:symbols owner :palette-track] track)
           ;; What is there already wins, so this fills a track in and can never
           ;; empty one.
           (update-in [:symbols track]
                      #(merge {:id track :name "palette" :type :palette-track
                               :display :lane :frames (clip/frames document owner)
                               :nodes {}}
                              %)))
       [:node track nil []]])
    [document target]))

(rf/reg-event-db
 ::new-symbol-at
 ;; ONE GESTURE AND ONE CREATION PATH FOR EVERY LANE. Empty space in a lane
 ;; takes a new one-frame held symbol at the frame double-clicked -- the pointer
 ;; names a place in time as well as a lane, which is the whole of the gesture
 ;; and the reason it is not the playhead's. The DESTINATION decides what is
 ;; created there: a clip of a palette track is a palette symbol whose
 ;; placement starts by inheriting, a clip of any other lane is a blank symbol.
 (fn [db [_ frame target]]
   (let [{document :clip st :store} (store/entry (:clip/current db))
         [document target] (lane-of-palette-row document target)
         where (drop-destination db document st frame target)
         palette? (= :palette-track (get-in document [:symbols (:sid where) :type]))
         sid (clip/fresh-id document)
         uuid (random-uuid)]
     (if (:refused where)
       (update db :project merge {:status (:refused where)})
       (let [seeded (assoc-in document [:symbols sid]
                              (cond-> {:id sid :frames 1 :nodes {}}
                                palette? (assoc :type :palette :name "palette transition")))
             result (span/place-symbol seeded st (:sid where) uuid sid (:at where)
                                       {:extent :grow-symbol
                                        :remainder-id (random-uuid)
                                        :speed 0})
             result (if-let [placed (and palette? (:clip result))]
                      (assoc result :clip
                             (assoc-in placed [:symbols (:sid where) :nodes uuid
                                               :channels [:palette]]
                                       (assoc (ch/framed pal/inherit) :semantic :palette)))
                      result)]
         (landed db where uuid result))))))

(defn- fitted
  "Tracing placement `uuid` in symbol `sid` of `document`, sized so its picture
  fits within `sid`'s stage, and — dropped on the timeline, with no point to
  land on — middled on that stage rather than wherever the picture's own middle
  happens to fall. A 1920px still is otherwise six stages tall.

  A DEFAULT, set once: nothing keeps it fitted afterwards.

  THE SCALE ALONE, SINCE A NODE HAS A PIVOT. `clip/place-symbol` has just put the
  picture's middle where it belongs and stored that same middle as the
  placement's `[:xform :pivot]`, and `node/local!` composes the scale about it —
  `T(pos)·T(a)·S(k)·T(-a)` leaves `a` exactly where it is for every `k` — so
  changing the scale here cannot move the picture off the point it was dropped
  on. This used to have to solve for the position again, the same equation
  `gesture/about` solves per drag, because there was no pivot to compose about
  and `T(pos)·S(k)` moves the middle by `(1-k)·middle`.

  A trace's own coordinates are its pixels, so a drop on the timeline still has
  a position to write: the picture's middle goes on the middle of the stage."
  [document sid uuid {:keys [width height]} point?]
  (let [[w h] (clip/stage document sid)
        k     (min (/ w width) (/ h height))
        mid   [(/ width 2) (/ height 2)]]
    (update-in document [:symbols sid :nodes uuid :channels]
               (fn [chs]
                 (cond-> (assoc chs [:xform :scale] (ch/framed [k k]))
                   (not point?)
                   (assoc [:xform :pos] (ch/framed (mapv - [(/ w 2) (/ h 2)] mid))))))))

(rf/reg-event-db
 ::drop-symbol
 ;; A symbol dropped on a row becomes a naturally playing clip in the symbol that
 ;; row leads into. Whether it claims the time it lands on is `span/place-symbol`'s
 ;; question and it answers it from the destination's display mode, so a drop onto
 ;; a lane trims its neighbour and a drop into a composition does not.
 (fn [db [_ source-id frame point target]]
   (let [{document :clip st :store} (store/entry (:clip/current db))
         where (if point
                 (creation-destination db document st frame)
                 (drop-destination db document st frame target))
         point (destination-point where point)
         uuid (random-uuid)]
     (if (:refused where)
       (-> db (update :ui dissoc :drop) (update :project merge {:status (:refused where)}))
       (let [sym (clip/symbol document source-id)
             result (span/place-symbol (:clip where) st (:sid where)
                                       uuid source-id (:at where)
                                       {:extent :grow-symbol :point point
                                        :remainder-id (random-uuid)})]
         (landed db where uuid
                 (cond-> result
                   (and (:clip result) (clip/trace? sym))
                   (update :clip fitted (:sid where) uuid sym (some? point)))))))))

(rf/reg-event-db
 ::drop-tracing
 ;; Footage or a still to draw over, as a tracing symbol — see `clip/trace?` —
 ;; placed by the same rule as any symbol dropped from the pool. One that shows
 ;; the same media already in the document is reused rather than copied: it is
 ;; the same picture, and a second symbol would only be a second name for it.
 ;;
 ;; A STILL ARRIVES WITH NO LENGTH, and is made as long as what is left of the
 ;; symbol it lands in: it shows the same picture on every frame, so its length
 ;; is only how long it lasts, and trimming is how that changes afterwards.
 ;;
 ;; FOOTAGE ARRIVES WITH ITS RATE, and the symbol does not keep it: its frames
 ;; are the footage's, and the placement plays them at the speed that makes them
 ;; real time here — 2.5 for 30fps footage in a 12fps project.
 (fn [db [_ {source-fps :fps :as sym} frame point target]]
   (let [{document :clip st :store} (store/entry (:clip/current db))
         where (if point
                 (creation-destination db document st frame)
                 (drop-destination db document st frame target))]
     (if (:refused where)
       (-> db (update :ui dissoc :drop) (update :project merge {:status (:refused where)}))
       (let [{host :sid at :at document :clip} where
             same (some (fn [[id s]] (when (= (:media s) (:media sym)) id)) (:symbols document))
             sid  (or same (clip/free-id #(contains? (:symbols document) %) :tracing))
             sym  (if same
                    (clip/symbol document same)
                    (cond-> (-> sym (dissoc :fps) (assoc :id sid))
                      (nil? (:frames sym)) (assoc :frames (max 1 (- (clip/frames document host) at)))))
             uuid (random-uuid)
             result (span/place-symbol (assoc-in document [:symbols sid] sym) st host
                                       uuid sid at
                                       {:extent :grow-symbol
                                        :speed (if source-fps (/ source-fps (:fps document)) 1)
                                        :point (destination-point where point)
                                        :remainder-id (random-uuid)})]
         (landed db where uuid
                 (cond-> result
                   (:clip result) (update :clip fitted host uuid sym (some? point)))))))))

(rf/reg-event-db
 ::drop-sound
 ;; A SOUND IS A CLIP TOO. It is placed and then adopted rather than written
 ;; straight in, so one command owns where a sound's frames are —
 ;; `clip/place-sound` — and one owns what claiming time means.
 (fn [db [_ {:keys [source label length rate]} frame target]]
   (let [{document :clip st :store} (store/entry (:clip/current db))
         where (drop-destination db document st frame target)
         uuid (random-uuid)]
     (if (:refused where)
       (-> db (update :ui dissoc :drop) (update :project merge {:status (:refused where)}))
       ;; `:at` IS ALREADY THE DESTINATION SYMBOL'S OWN FRAME, which is what
       ;; `place-sound` positions in. It used to be multiplied by the grid rate
       ;; again on the way in — a second conversion of an already-converted
       ;; number, which dropped a sound 2.5 frames late for every frame it was
       ;; selected in a 12fps project.
       (let [sid (:sid where)
             seeded (clip/place-sound (:clip where) sid source label length rate
                                      (:at where) uuid)]
         (landed db where uuid
                 (span/adopt seeded sid uuid (:at where)
                             {:extent :grow-symbol :remainder-id (random-uuid)})))))))

(rf/reg-event-db
 ::drop-clip
 ;; A clip body is already materialized, so its whole drop is: resolve the
 ;; destination, then transfer it. `span/transfer` asks the destination whether
 ;; it claims time; this event does not know or care whether that is a lane.
 (fn [db [_ [_ from-sid id _] target frame]]
   (let [{document :clip st :store} (store/entry (:clip/current db))
         where (drop-destination db document st frame target)
         result (when-not (:refused where)
                  (span/transfer document from-sid id (:sid where) (:at where)
                                 {:extent :grow-symbol
                                  :remainder-id (random-uuid)}))]
     (if-let [why (or (:refused where) (:refused result))]
       (update db :project merge {:status why})
       (landed db where id result)))))

;; ---------------------------------------------------------------------------
;; moving rows between symbols
;;
;; Both are `nest/move-node` and `nest/group`, which keep the picture and the
;; timing as they are; what these add is where the selection goes and the reason
;; when a move is refused, which is the only feedback a refused drop has.

(defn- refused [db why]
  (update db :project merge {:status (str "can't: " why)}))

(rf/reg-event-db
 ::move-node
 (fn [db [_ from to]]
   (let [{clip :clip st :store} (store/entry (:clip/current db))
         r (nest/move-node clip st (get-in db [:ui :open]) from to
                           (editing-frame db clip))]
     (if-let [why (:refused r)]
       (refused db why)
       (-> db
           (edit/edit (constantly (:clip r)))
           (assoc-in [:ui :selection] [:node (:sid r) (:id r) (conj to (:id r))])
           (cond-> (not= :audio (get-in r [:clip :symbols (:sid r) :nodes (:id r) :kind]))
             (update-in [:ui :expanded] into (rest (reductions conj [] to)))))))))

(rf/reg-event-db
 ::sliding
 ;; A bar in the middle of a slide, drawn by `::render/clip`; nil path when the
 ;; drag is abandoned.
 (fn [db [_ path df kind ripple? other owner paths]]
   (if path
     (assoc-in db [:ui :sliding] {:path path :df df :kind (or kind :slide)
                                  :ripple? (boolean ripple?) :other other :owner owner :paths paths})
     (update db :ui dissoc :sliding))))

(rf/reg-event-db
 ::slide
 (fn [db [_ path df kind ripple? other owner paths]]
   (let [db (update db :ui dissoc :sliding)
         clip (:clip (store/entry (:clip/current db)))
         open (or owner (get-in db [:ui :open]))
         r  (nest/drag clip open {:path path :df df :kind kind :ripple? ripple?
                                  :other other :paths paths})]
     (cond
       (zero? df) db
       (:refused r) (refused db (:refused r))
       :else (edit/transaction db (constantly (:clip r)))))))

(rf/reg-event-db
 ::refuse
 (fn [db [_ why]] (refused db why)))

(rf/reg-event-db
 ::gesture
 ;; A transform in the middle of a drag on the stage, `{:sid :id :frame
 ;; :values}`, drawn by `::render/clip` as `::sliding` is; nil when abandoned.
 ;; Auto-key SAMPLES once per playback frame but does not EDIT once per frame.
 ;; Samples stay in the gesture's in-memory `:take`; pointer events only replace
 ;; the current frame's sample. Pointer-up commits the complete take once.
 (fn [db [_ g]]
   (let [active (get-in db [:ui :gesture])
         auto?  (if active (:auto-key? active) (boolean (get-in db [:ui :auto-key?])))]
     (if g
       (let [g  (merge active g {:auto-key? auto?})
             db (assoc-in db [:ui :gesture] g)]
         ;; Materialize the current frame immediately. More pointer moves in the
         ;; same frame only replace the preview; pointer-up forces its last value.
         (if auto?
           (record-auto-frame db (get-in db [:playback :frame]))
           db))
       (update db :ui dissoc :gesture)))))

(defn record-auto-frame
  "Buffer the active gesture at output playback frame `f`, mapped to the node's
  local frame. Repeated pointer events replace that frame's sample cheaply.

  `f` is the TRANSPORT's frame here, because this is called from the pointer
  stream while something plays, and the gesture names the symbol it started in —
  so the crossing into that symbol's own frames happens here, against that
  symbol, rather than in the caller against whatever happens to be open."
  [db f]
  (let [{:keys [auto-key? open path values] :as g}
        (get-in db [:ui :gesture])]
    (if (and auto-key? (seq values))
      (let [{document :clip st :store} (store/entry (:clip/current db))]
        (if-let [{:keys [sid id frame]} (nest/placement document st open path
                                                        f)]
          (assoc-in db [:ui :gesture]
                    (-> g
                        (assoc :sid sid :id id :frame frame)
                        (assoc-in [:take [sid id] frame] values)))
          db))
      db)))

(rf/reg-event-db
 ::record-gesture
 (fn [db [_ f]] (record-auto-frame db f)))

(rf/reg-event-db
 ::transform
 ;; The drag let go: one edit, so one undo step and one write to collaborators.
 (fn [db [_ g]]
   (let [auto? (boolean (get-in db [:ui :gesture :auto-key?]))
         st (:store (store/entry (:clip/current db)))]
     (if auto?
       (let [db   (-> db
                      (update-in [:ui :gesture] merge g)
                      (record-auto-frame (get-in db [:playback :frame])))
             take (get-in db [:ui :gesture :take])]
         (cond-> (update db :ui dissoc :gesture)
           (seq take) (edit/edit #(gesture/apply-take % take st))))
       (let [{:keys [sid id frame values]} g]
         (cond-> (update db :ui dissoc :gesture)
           (seq values) (edit/edit #(gesture/apply-values % sid id frame values false st))))))))

(rf/reg-event-db
 ::transform-many
 ;; Every visible member of a stage selection is one edit and therefore one
 ;; undo step. Members absent at this playhead are intentionally not in `edits`.
 (fn [db [_ edits]]
   (let [st (:store (store/entry (:clip/current db)))]
     (cond-> (update db :ui dissoc :gesture)
       (seq edits)
       (edit/edit #(reduce (fn [c {:keys [sid id frame values]}]
                             (gesture/apply-values c sid id frame values
                                                   (boolean (get-in db [:ui :auto-key?])) st))
                           % edits))))))

(rf/reg-fx
 ::confirm
 (fn [{:keys [message then]}]
   (when (js/confirm message) (rf/dispatch then))))

(rf/reg-event-fx
 ::delete-symbols
 ;; Out of the library, with their placements; see `nest/delete-symbol`. Their
 ;; tabs close, and if one was on screen the root takes its place.
 ;;
 ;; ASKED FIRST WHEN ANY IS PLACED, because then the delete reaches past the
 ;; rows you pointed at, into every symbol that uses them. Ones placed nowhere
 ;; go without a question; undo is there either way.
 (fn [{:keys [db]} [_ sids sure?]]
   (let [clip (:clip (store/entry (:clip/current db)))
         sids (vec (distinct sids))
         {gone :clip why :refused} (reduce (fn [r sid]
                                             (let [r (nest/delete-symbol (:clip r) sid)]
                                               (if (:refused r) (reduced r) r)))
                                           {:clip clip} sids)
         gone? (set sids)
         ;; Uses from outside what is being deleted: those are what reach past.
         placed (for [sid sids
                      :let [owners (remove gone? (clip/owners clip sid))]
                      :when (seq owners)]
                  [sid owners])]
     (cond
       why
       {:db (refused db why)}

       (and (seq placed) (not sure?))
       {::confirm {:message (str "Delete "
                                 (if (= 1 (count sids))
                                   (clip/symbol-name clip (first sids))
                                   (str (count sids) " symbols"))
                                 "? "
                                 (str/join "; " (for [[sid owners] placed]
                                                  (str (clip/symbol-name clip sid) " is used in "
                                                       (str/join ", " (map #(clip/symbol-name clip %) owners)))))
                                 ", and every use goes too.")
                   :then [::delete-symbols sids true]}}

       :else
       (cond-> {:db (-> db
                        (edit/transaction (constantly gone))
                        (update-in [:ui :tabs] #(vec (remove gone? %)))
                        (update :ui (fn [ui]
                                      (let [left (vec (remove #(and (= :symbol (first %)) (gone? (second %)))
                                                              (:selections ui)))]
                                        (assoc ui :selections left
                                               :selection (when (some #{(:selection ui)} left)
                                                            (:selection ui)))))))}
         (gone? (get-in db [:ui :open]))
         (assoc :dispatch [::playback/open-symbol (clip/opens-on gone)]))))))

(rf/reg-event-fx
 ::delete-selected
 ;; Backspace while drawing takes the last point back, as in Illustrator; on
 ;; the pen, with a point of the selected shape picked, it takes that point.
 ;; Symbols picked in the pool are deleted from the library.
 (fn [{:keys [db]} _]
   (let [[vsid vid i] (get-in db [:ui :vertex])
         primary (get-in db [:ui :selection])]
     (cond
       (seq (get-in db [:ui :draft]))
       {:db (update-in db [:ui :draft] #(subvec % 0 (- (count %) 2)))}

       (and i (= :pen (get-in db [:ui :tool])) (= [:node vsid vid] (take 3 primary)))
       {:db (deleting-vertex db vsid vid i)}

       (= :symbol (first primary))
       {:dispatch [::delete-symbols (keep (fn [[kind sid]] (when (= :symbol kind) sid))
                                          (cons primary (get-in db [:ui :selections])))]}

       :else
       (let [many (vec (get-in db [:ui :selections]))
             selections (if (some #{primary} many) many (if primary [primary] []))
             nodes (distinct (keep (fn [[kind sid id]] (when (= :node kind) [sid id])) selections))]
         (when (seq nodes)
           {:db (-> db
                    (edit/transaction #(reduce (fn [c [sid id]] (nest/delete-node c sid id)) % nodes))
                    (assoc-in [:ui :selection] nil)
                    (assoc-in [:ui :selections] []))}))))))

(rf/reg-event-db
 ::restack
 ;; Onto the edge of a row in another symbol, it goes into that symbol first:
 ;; one gesture, as in a layers panel, for where it lives and where in the stack.
 (fn [db [_ from to front?]]
   (let [{clip :clip st :store} (store/entry (:clip/current db))
         open  (get-in db [:ui :open])
         f     (editing-frame db clip)
         host  (pop to)
         moved (if (= host (pop from))
                 {:clip clip :id (peek from)}
                 (nest/move-node clip st open from host f))
         r     (if (:refused moved)
                 moved
                 (nest/restack (:clip moved) open (conj host (:id moved)) to front?))]
     (if-let [why (:refused r)]
       (refused db why)
       (-> db
           (edit/edit (constantly (:clip r)))
           (assoc-in [:ui :selection] [:node (:sid r) (:id r) (conj host (:id r))])
           (update-in [:ui :expanded] into (rest (reductions conj [] host))))))))

(rf/reg-event-db
 ::group
 (fn [db [_ froms]]
   (let [{clip :clip st :store} (store/entry (:clip/current db))
         open (get-in db [:ui :open])
         f    (editing-frame db clip)
         host (pop (first froms))
         uuid (random-uuid)
         r    (nest/group clip st open froms (clip/fresh-id clip) uuid f)]
     (if-let [why (:refused r)]
       (refused db why)
       (-> db
           (edit/edit (constantly (:clip r)))
           (assoc-in [:ui :selection] [:node (:sid (nest/inside clip st open host f))
                                       uuid (conj host uuid)])
           (update-in [:ui :expanded] conj (conj host uuid)))))))

(rf/reg-event-db
 ::peg
 ;; A peg over the selected node, selected afterwards — because the point of
 ;; making one is to transform it, and the thing you just made is the thing you
 ;; meant to grab. The peg takes the node's place in the hierarchy, so the row
 ;; path it is selected by is the one the node had.
 (fn [db [_ path]]
   (let [{clip :clip st :store} (store/entry (:clip/current db))
         open (get-in db [:ui :open])
         f    (editing-frame db clip)
         uuid (random-uuid)
         r    (nest/peg clip st open path f uuid)]
     (if-let [why (:refused r)]
       (refused db why)
       (-> db
           (edit/edit (constantly (:clip r)))
           (assoc-in [:ui :selection] [:node (:sid r) uuid (conj (pop (vec path)) uuid)])))))) 

(rf/reg-event-db
 ::repivot
 ;; Put the pivot of the node at `path` on stage point `point`, moving nothing.
 ;; One edit at the end of the drag rather than per pointermove: a repivot moves
 ;; nothing on screen by construction, so there is no intermediate state worth
 ;; drawing — `ui/stage` follows the pointer with the cross alone.
 ;;
 ;; ANY NODE, not just a peg. `[:xform :pivot]` is a channel every node has, so
 ;; this is the same two writes — the pivot, and the position that leaves the
 ;; picture where it is — on a drawing, an instance, a peg or a measured part
 ;; alike. It used to be `nest/repivot`, which moved a peg's ORIGIN and
 ;; compensated each child's `:pinv` to hold it still: the same gesture by way of
 ;; the parenting machinery, available on pegs alone, and refused outright on a
 ;; peg whose position was animated, because one stored parent-inverse cannot
 ;; correct for a parent that moves. A pivot in the node's own transform has
 ;; nothing to correct in anybody else's.
 ;;
 ;; NOT REFUSED ON A MEASURED NODE, though a drag on one is. A pivot is authored
 ;; on every node — never dense, never regenerated — so a measured mouth can be
 ;; told where to turn about without a peg and with nothing a regenerate would
 ;; throw away. The compensating position goes through `apply-values`, which puts
 ;; it in a correction layer over the measurement, exactly as a hand move does.
 (fn [db [_ path point]]
   (let [{clip :clip st :store} (store/entry (:clip/current db))
         open (get-in db [:ui :open])
         f    (editing-frame db clip)
         pl   (nest/placement clip st open (vec path) f)
         c    (when pl (let [out (js/Float64Array. 2)]
                         (when-let [inv (node/invert (:parent pl))]
                           (node/apply-pt! out 0 inv (first point) (second point))
                           [(aget out 0) (aget out 1)])))
         vs   (when c (gesture/repivot
                       (gesture/values (get-in clip [:symbols (:sid pl) :nodes (:id pl)])
                                       (:frame pl) st)
                       c))]
     (cond
       (nil? vs) db
       :else (edit/edit db #(gesture/apply-values % (:sid pl) (:id pl) (:frame pl)
                                                  vs false st))))))

(rf/reg-event-db
 ::centre-pivot
 ;; Put the pivot of the node at `path` back on the middle of what it draws NOW,
 ;; moving nothing. The inspector's ⌖, beside the pivot row.
 ;;
 ;; THE WAY BACK FROM A STORED PIVOT, and what makes storing one safe. A pivot is
 ;; a choice and does not follow the drawing — a keyed spin must not be re-aimed
 ;; by somebody drawing one more shape inside the symbol — but drawings do grow,
 ;; and re-centring by hand means reading a bounding box off the screen and typing
 ;; two numbers that also have to move `pos` to avoid shifting the picture. One
 ;; button, `gesture/centred`, at exactly the point an untouched node's cross
 ;; would already be.
 (fn [db [_ path]]
   (let [{clip :clip st :store} (store/entry (:clip/current db))
         open (get-in db [:ui :open])
         f    (editing-frame db clip)
         pl   (nest/placement clip st open (vec path) f)
         n    (when pl (get-in clip [:symbols (:sid pl) :nodes (:id pl)]))
         vs   (when n (gesture/centred
                       (gesture/values n (:frame pl) st)
                       ((pick/bounds-of clip st (:sid pl) n) (:frame pl))))]
     (if (nil? vs)
       (refused db "it is not on screen at this frame, so it has no middle to find")
       (edit/edit db #(gesture/apply-values % (:sid pl) (:id pl) (:frame pl) vs false st)))))) 

(rf/reg-event-db
 ::edit-keyframes
 (fn [db [_ items delta]]
   (edit/transaction db #(keyframes/edit-keys % items delta))))

(rf/reg-event-db
 ::move-nodes
 (fn [db [_ selections to frame delta]]
   (let [{document :clip st :store} (store/entry (:clip/current db))
         result (clipboard/move-many document st (get-in db [:ui :open]) selections to
                                     (or frame (editing-frame db document)) delta)]
     (if-let [why (:refused result)]
       (refused db why)
       (-> db
           (update :ui dissoc :sliding :drop)
           (edit/transaction (constantly (:clip result)))
           (selected (peek (:selections result)))
           (assoc-in [:ui :selections] (:selections result))
           (update-in [:ui :expanded] (fnil into #{}) (rest (reductions conj [] to))))))))

(rf/reg-event-db
 ::restack-nodes
 (fn [db [_ selections to front?]]
   (let [{document :clip st :store} (store/entry (:clip/current db))
         open (get-in db [:ui :open])
         host (pop to)
         roots (clipboard/canonical document selections)
         foreign (mapv :address (remove #(= host (pop (:path %))) roots))
         moved (if (seq foreign)
                 (clipboard/move-many document st open foreign host (editing-frame db document) 0)
                 {:clip document :selections []})
         addresses (vec (concat (map :address (filter #(= host (pop (:path %))) roots))
                                (:selections moved)))
         result (reduce (fn [r address]
                          (if (:refused r) (reduced r)
                            (nest/restack (:clip r) open (nth address 3) to front?)))
                        moved (if front? (reverse addresses) addresses))]
     (if-let [why (:refused result)]
       (refused db why)
       (-> db
           (edit/transaction (constantly (:clip result)))
           (selected (peek addresses))
           (assoc-in [:ui :selections] addresses)
           (update-in [:ui :expanded] (fnil into #{}) (rest (reductions conj [] host))))))))
